import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Loyaltic } from './api-url.constants';
import {
  ApiResponse,
  DailyRegistrationTrend,
  WeeklyRegistrationTrend,
  RegistrationSummary,
  DailyMarketingOptInTrend,
  WeeklyMarketingOptInTrend,
  MarketingEmailOptInSummary,
  DailySlotStatusTrend,
  WeeklySlotStatusTrend,
  SlotStatusSummary,
  DailyGamePlayTrend,
  WeeklyGamePlayTrend,
  GamePlaySummary,
  DailyRewardTrend,
  WeeklyRewardTrend,
  RewardSummary
} from '../models/analytics.models';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  constructor(private http: HttpClient) { }

  // User Registration Analytics
  getRegistrationsDailyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<DailyRegistrationTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<DailyRegistrationTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.RegistrationsDailyTrend,
      { headers, params }
    );
  }

  getRegistrationsWeeklyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<WeeklyRegistrationTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<WeeklyRegistrationTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.RegistrationsWeeklyTrend,
      { headers, params }
    );
  }

  getRegistrationsSummary(password: string): Observable<ApiResponse<RegistrationSummary>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    return this.http.get<ApiResponse<RegistrationSummary>>(
      environment.baseUrl + Loyaltic.API.Analytics.RegistrationsSummary,
      { headers }
    );
  }

  // Marketing Email Opt-In Analytics
  getMarketingEmailsDailyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<DailyMarketingOptInTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<DailyMarketingOptInTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.MarketingEmailsDailyTrend,
      { headers, params }
    );
  }

  getMarketingEmailsWeeklyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<WeeklyMarketingOptInTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<WeeklyMarketingOptInTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.MarketingEmailsWeeklyTrend,
      { headers, params }
    );
  }

  getMarketingEmailsSummary(password: string): Observable<ApiResponse<MarketingEmailOptInSummary>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    return this.http.get<ApiResponse<MarketingEmailOptInSummary>>(
      environment.baseUrl + Loyaltic.API.Analytics.MarketingEmailsSummary,
      { headers }
    );
  }

  // Slot Status Analytics
  getSlotsDailyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<DailySlotStatusTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<DailySlotStatusTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.SlotsDailyTrend,
      { headers, params }
    );
  }

  getSlotsWeeklyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<WeeklySlotStatusTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<WeeklySlotStatusTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.SlotsWeeklyTrend,
      { headers, params }
    );
  }

  getSlotsSummary(password: string): Observable<ApiResponse<SlotStatusSummary>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    return this.http.get<ApiResponse<SlotStatusSummary>>(
      environment.baseUrl + Loyaltic.API.Analytics.SlotsSummary,
      { headers }
    );
  }

  // Game Play Analytics
  getGamesDailyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<DailyGamePlayTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<DailyGamePlayTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.GamesDailyTrend,
      { headers, params }
    );
  }

  getGamesWeeklyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<WeeklyGamePlayTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<WeeklyGamePlayTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.GamesWeeklyTrend,
      { headers, params }
    );
  }

  getGamesSummary(password: string): Observable<ApiResponse<GamePlaySummary>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    return this.http.get<ApiResponse<GamePlaySummary>>(
      environment.baseUrl + Loyaltic.API.Analytics.GamesSummary,
      { headers }
    );
  }

  // Reward Analytics
  getRewardsDailyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<DailyRewardTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<DailyRewardTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.RewardsDailyTrend,
      { headers, params }
    );
  }

  getRewardsWeeklyTrend(password: string, startDate?: Date, endDate?: Date): Observable<ApiResponse<WeeklyRewardTrend>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate.toISOString());
    }
    if (endDate) {
      params = params.set('endDate', endDate.toISOString());
    }
    return this.http.get<ApiResponse<WeeklyRewardTrend>>(
      environment.baseUrl + Loyaltic.API.Analytics.RewardsWeeklyTrend,
      { headers, params }
    );
  }

  getRewardsSummary(password: string): Observable<ApiResponse<RewardSummary>> {
    const headers = new HttpHeaders().set('Analytics-Password', password);
    return this.http.get<ApiResponse<RewardSummary>>(
      environment.baseUrl + Loyaltic.API.Analytics.RewardsSummary,
      { headers }
    );
  }
}
